/**
 * @fileoverview Bulk Approve mutation hook.
 * @description Approves up to 50 pending requests in one call (POST /approvals/bulk-approve), each
 * with the version it was listed at, and refreshes every sales query once it answers. The answer
 * splits the batch into the approved and the skipped, so a partly stale batch is a success the
 * caller reports, not an error.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useIdempotentMutation } from '@/features/sales/hooks/useIdempotentMutation'
import { bulkApproveApprovalRequests } from '@/features/sales/services/approvals.service'

import type { BulkApproveItem, BulkApproveResult } from '@/features/sales/types/approval.types'

/** The input of one bulk-approve command. */
export interface BulkApproveInput {
  items: BulkApproveItem[]
  reviewNote: string | null
}

/**
 * @description Approves a batch of pending review requests. The command's idempotency key outlives
 * the dialog, so a retry after an unknown outcome replays it.
 * @param {object} [options] - What follows a success.
 * @param {(result: BulkApproveResult, input: BulkApproveInput) => void} [options.onSuccess] -
 * Called with the outcome, even when the dialog has unmounted since.
 * @returns {ReturnType<typeof useIdempotentMutation>} The mutation (mutate with the items and an
 * optional note).
 */
export function useBulkApproveApprovals({
  onSuccess,
}: {
  onSuccess?: ((result: BulkApproveResult, input: BulkApproveInput) => void) | undefined
} = {}) {
  return useIdempotentMutation<BulkApproveInput, BulkApproveResult>({
    action: 'bulk-approve',
    /**
     * @description Approves the batch under the key it is given.
     * @param {BulkApproveInput} input - The requests, each at the version it was shown at, and the shared note.
     * @param {string} key - The Idempotency-Key.
     * @returns {Promise<BulkApproveResult>} The outcome per request.
     */
    send: ({ items, reviewNote }, key) => bulkApproveApprovalRequests(items, reviewNote, key),
    onSuccess,
  })
}
